import { Avatar, Button, EmptyState, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2Off } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { Logo } from '../../components/Logo';
import { api } from '../../lib/api';
import { keys } from '../../lib/queries';
import { FullScreenSpinner } from '../auth/RequireAuth';
import { errorMessage } from '../../lib/forms';

/** Landing page for shareable invite links: /join/:token */
export function JoinPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const preview = useQuery({
    queryKey: ['join', token],
    queryFn: () => api.inviteLinks.preview(token),
    retry: false,
  });
  const join = useMutation({
    mutationFn: () => api.inviteLinks.join(token),
    onSuccess: async (team) => {
      await queryClient.invalidateQueries({ queryKey: keys.teams });
      toast({ title: t('Te has unido a {{team}}', { team: team.name }), tone: 'success' });
      void navigate(`/app/t/${team.id}`, { replace: true });
    },
    onError: (err) =>
      toast({
        title: t('No se pudo unir al equipo'),
        description: errorMessage(err),
        tone: 'error',
      }),
  });

  if (preview.isPending) return <FullScreenSpinner />;

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-canvas px-4 py-10">
      <Logo className="mb-8" />
      {preview.isError ? (
        <EmptyState
          className="max-w-md rounded-2xl border border-border bg-surface shadow-panel"
          icon={<Link2Off className="size-8" />}
          title={t('Enlace no válido')}
          description={errorMessage(preview.error)}
          action={
            <Link to="/app" className="text-sm font-medium text-accent hover:underline">
              {t('Ir a la app')}
            </Link>
          }
        />
      ) : (
        <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-8 text-center shadow-panel">
          <Avatar
            id={preview.data.teamId}
            name={preview.data.teamName}
            shape="square"
            size="lg"
            className="mx-auto"
          />
          <p className="mt-5 text-sm text-muted">
            {preview.data.invitedBy
              ? t('{{name}} te invita a unirte a', { name: preview.data.invitedBy.displayName })
              : t('Te han invitado a unirte a')}
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{preview.data.teamName}</h1>
          <p className="mt-1 text-sm text-subtle">
            {preview.data.memberCount === 1
              ? t('1 miembro')
              : t('{{count}} miembros', { count: preview.data.memberCount })}
          </p>
          {preview.data.alreadyMember ? (
            <Button
              size="lg"
              className="mt-6 w-full"
              onClick={() => void navigate(`/app/t/${preview.data.teamId}`)}
            >
              {t('Ya eres miembro · Abrir equipo')}
            </Button>
          ) : (
            <Button
              size="lg"
              className="mt-6 w-full"
              loading={join.isPending}
              onClick={() => join.mutate()}
            >
              {t('Unirme al equipo')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
